import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  AnchoredMenu,
  Avatar,
  resolveTheme,
  SettingsMenuView,
  useDisclosure,
  type AnchoredMenuAlign,
  type AnchoredMenuPlacement,
  type SettingsMenuSection,
  type UiTheme,
} from "@heraion/ui";

import { useThemePalette } from "./theme";
import type { MeKey } from "./shell/meNav";
import { stateAria } from "@heraion/ui";

// The web account menu (shell chrome): the identity slot, top-right in the
// TopBar. It gathers "about me and my account": the Me sections and sign-out.
// The role switch and Settings left it for the experience row under the TopBar
// (#208), the same row the phone has, so the menu no longer duplicates them.
//
// Presentation is the shared SettingsMenuView in `dense` mode (a tight menu
// list, not description cards), driven by the meSub (open Me section).
// Selecting a row dispatches to one of the two typed callbacks the shell owns.

export function AccountMenu({
  email,
  activeKey,
  onSelectMe,
  onSignOut,
  placement,
  align,
  compact = false,
}: {
  email: string;
  /** The open Me section (highlighted), or null. */
  activeKey: MeKey | null;
  onSelectMe: (key: MeKey) => void;
  onSignOut: () => void;
  placement: AnchoredMenuPlacement;
  align: AnchoredMenuAlign;
  /** Narrow header trigger: avatar only, no email label. */
  compact?: boolean;
}) {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t } = useTranslation();
  const disc = useDisclosure();
  const styles = makeStyles(theme);

  const close = () => disc.setExpanded(false);

  const meSection: SettingsMenuSection = {
    label: t("mobile.tabs.me", { defaultValue: "Me" }),
    rows: [
      // Mobile Me's order (#315): Goals, MAXes, then Body, then Coach.
      { key: "targets", title: t("mobile.me.navTargets", { defaultValue: "Goals" }), sub: "" },
      { key: "maxes", title: t("mobile.profile.navMaxes", { defaultValue: "MAXes" }), sub: "" },
      {
        key: "corpo",
        title: t("mobile.body.measurementsTitle", { defaultValue: "Measurements" }),
        sub: "",
      },
      { key: "painMap", title: t("bodymap.painTitle"), sub: "" },
      { key: "coach", title: t("coach.title", { defaultValue: "Coach & athletes" }), sub: "" },
    ],
  };

  // Sign-out rides the SAME dense SettingsMenuView (no hand-rolled rows). It is
  // an action, not a selectable section, so no selectedKey matches it and the
  // accent bar stays idle. onSelect dispatches by key.
  const accountSection: SettingsMenuSection = {
    label: t("mobile.settings.sectionAccount", { defaultValue: "Account" }),
    rows: [
      { key: "signout", title: t("app.signOut"), sub: "" },
    ],
  };

  return (
    <AnchoredMenu
      theme={theme}
      open={disc.expanded}
      onClose={close}
      dismissLabel={t("mobile.common.close", { defaultValue: "Close" })}
      placement={placement}
      align={align}
      minWidth={300}
      trigger={
        <Pressable
          onPress={disc.toggle}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={email}
          accessibilityState={{
            expanded: disc.expanded,
            selected: activeKey !== null,
          }}
          {...stateAria({ expanded: disc.expanded, selected: activeKey !== null }, "button")}
          style={({ pressed }) => [
            styles.trigger,
            compact && styles.triggerCompact,
            (pressed || disc.expanded) && styles.triggerOn,
          ]}
        >
          <Avatar theme={theme} name={email} size={32} />
          {compact ? null : (
            <Text style={styles.triggerLabel} numberOfLines={1}>
              {email}
            </Text>
          )}
          {/* "A Me sub-surface is open" - a persistent accent dot, a
              shape cue distinct from the transient pressed fill (never colour
              alone). */}
          {activeKey !== null ? <View style={styles.activeDot} /> : null}
        </Pressable>
      }
    >
      <View style={styles.menuBody}>
        <SettingsMenuView
          theme={theme}
          dense
          sections={[meSection, accountSection]}
          // Highlights the open Me section, so re-opening the menu says
          // "you are here", never state-by-absence. Sign-out never matches.
          selectedKey={activeKey}
          onSelect={(key) => {
            close();
            if (key === "signout") onSignOut();
            else onSelectMe(key as MeKey);
          }}
        />
      </View>
    </AnchoredMenu>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    trigger: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s3,
      minHeight: 44,
      paddingHorizontal: s.s2,
      borderRadius: r.r4,
      borderWidth: 1,
      borderColor: "transparent",
    },
    triggerCompact: { paddingHorizontal: s.s1 },
    triggerOn: { backgroundColor: p.surface2, borderColor: p.border },
    triggerLabel: { flex: 1, minWidth: 0, color: p.ink2, fontSize: ty.sizes.sm },
    activeDot: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: p.brand500,
    },
    menuBody: { paddingHorizontal: s.s2 },
  });
}
